/**
 * Budget workspace — "The Reconciliation Rail".
 *
 * A single un-tabbed workspace driven by a left pipeline rail
 * (Seed → Import → Reconcile → Review → Export), replacing the old 6 sub-tabs
 * + wizard nesting (nav depth 4 → 2). Each stage is a focused screen; the
 * disposition taxonomy + foot-to-gross bridge are the spine of Reconcile + Review.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, AlertTriangle, Upload, Download,
  Loader2, CheckCircle, Building2,
  FileSpreadsheet, RefreshCw, Target, Edit3, ArrowRight, CircleCheck,
  ChevronLeft, ChevronRight, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import { useAuth } from '@/providers/AuthProvider';
import { hasRole, BUDGET_ROLES } from '@/auth/permissions';
import {
  budgetService,
  BudgetSummary,
  BudgetImportPreviewResponse,
  BudgetImportResult,
  VacancyItem,
  DISPOSITION_META,
  BudgetDisposition,
} from '@/services/api/organizationService';
import BudgetImportWizard from './BudgetImportWizard';
import BudgetVsActuals from './BudgetVsActuals';
import BudgetPivot from './BudgetPivot';
import BudgetWorkbench from './BudgetWorkbench';
import StagedItemsView from './StagedItemsView';
import PositionBudgetView from './PositionBudgetView';
import BudgetRail, { RailStage } from './pipeline/BudgetRail';
import {
  ReconciliationWaterfall, countsFromItems, groupCounts,
  GROUP_FILL, GROUP_ORDER, GROUP_LABEL, DispositionChip, DispositionCount,
} from './DispositionChip';

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-ZA').format(n);
}

// Employment-type (RF1 payroll type) facet — canonical categories + tones.
const EMP_ORDER = ['Permanent', 'FTC', 'ICA', 'Casual', 'Learnership', 'Intern', 'Unspecified'];
const EMP_TONE: Record<string, string> = {
  Permanent: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FTC: 'bg-sky-50 text-sky-700 ring-sky-200',
  ICA: 'bg-amber-50 text-amber-700 ring-amber-200',
  Casual: 'bg-rose-50 text-rose-700 ring-rose-200',
  Learnership: 'bg-slate-100 text-slate-700 ring-slate-200',
  Intern: 'bg-violet-50 text-violet-700 ring-violet-200',
};

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STAGES = ['seed', 'import', 'reconcile', 'review', 'export'] as const;
type Stage = typeof STAGES[number];

// ── Reusable Breakdown Table ────────────────────────────────────────────

interface BreakdownEntry {
  code?: string | null;
  name: string;
  positions: number;
  filled: number;
  vacant: number;
  budgeted_cost: number;
  actual_cost?: number;
}

const BreakdownTable: React.FC<{ title: string; data: BreakdownEntry[] }> = ({ title, data }) => {
  if (!data || data.length === 0) return null;
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold">{title} Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow className="text-xs">
              <TableHead>{title}</TableHead>
              <TableHead className="text-right">Positions</TableHead>
              <TableHead className="text-right">Filled</TableHead>
              <TableHead className="text-right">Vacant</TableHead>
              <TableHead className="text-right">Budgeted Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.code ?? row.name}
                className={cn("text-sm", !row.code && "text-gray-400 italic")}
              >
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell className="text-right">{row.positions}</TableCell>
                <TableCell className="text-right text-emerald-600">{row.filled}</TableCell>
                <TableCell className="text-right">
                  {row.vacant > 0 ? (
                    <span className="text-amber-600">{row.vacant}</span>
                  ) : (
                    <span className="text-gray-300">0</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {row.budgeted_cost > 0 ? formatCurrency(row.budgeted_cost) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ── Vacancies Panel ─────────────────────────────────────────────────────

const VacanciesPanel: React.FC<{
  vacantBudgeted: number;
  vacantUnbudgeted: number;
  budgetYear?: number;
  budgetMonth?: number;
}> = ({ vacantBudgeted, vacantUnbudgeted, budgetYear, budgetMonth }) => {
  const [category, setCategory] = useState<'budgeted' | 'unbudgeted'>('budgeted');
  const [items, setItems] = useState<VacancyItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [reasons, setReasons] = useState<{ backfill: number; new: number; total_vacant: number } | null>(null);

  useEffect(() => {
    budgetService.getVacanciesWithReason()
      .then((r) => setReasons({ backfill: r.backfill, new: r.new, total_vacant: r.total_vacant }))
      .catch(() => { /* best-effort — seat history may not be synced yet */ });
  }, []);

  const loadVacancies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await budgetService.getVacancies({
        year: budgetYear, month: budgetMonth, category, limit: 500,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      console.error('Failed to load vacancies:', e);
    } finally {
      setLoading(false);
    }
  }, [budgetYear, budgetMonth, category]);

  useEffect(() => { loadVacancies(); }, [loadVacancies]);

  const filtered = searchFilter
    ? items.filter(v => {
        const q = searchFilter.toLowerCase();
        return (
          v.title?.toLowerCase().includes(q) ||
          v.fo_position_id?.toLowerCase().includes(q) ||
          v.dimension_campus?.toLowerCase().includes(q) ||
          v.dimension_department?.toLowerCase().includes(q) ||
          v.job_family?.toLowerCase().includes(q) ||
          v.campus_name?.toLowerCase().includes(q)
        );
      })
    : items;

  return (
    <div className="space-y-3">
      {/* Category toggle */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setCategory('budgeted')}
          className={cn(
            "rounded-md border p-3 text-left transition-colors",
            category === 'budgeted' ? 'ring-2 ring-amber-400 bg-amber-50/50' : 'hover:bg-gray-50',
          )}
        >
          <div className="text-xl font-bold text-amber-600">{vacantBudgeted}</div>
          <div className="text-xs text-gray-500">Budgeted & Vacant</div>
          <div className="text-[10px] text-gray-400 mt-0.5">Prioritize for filling</div>
        </button>
        <button
          onClick={() => setCategory('unbudgeted')}
          className={cn(
            "rounded-md border p-3 text-left transition-colors",
            category === 'unbudgeted' ? 'ring-2 ring-red-400 bg-red-50/50' : 'hover:bg-gray-50',
          )}
        >
          <div className="text-xl font-bold text-red-500">{vacantUnbudgeted}</div>
          <div className="text-xs text-gray-500">Unbudgeted & Vacant (Stale)</div>
          <div className="text-[10px] text-gray-400 mt-0.5">Candidates for retirement</div>
        </button>
      </div>

      {reasons && (
        <div className="flex flex-wrap items-center gap-3 text-xs rounded-md bg-slate-50 ring-1 ring-slate-100 px-3 py-2">
          <span className="text-slate-500">Vacancy reason (from seat history):</span>
          <span className="inline-flex items-center gap-1.5 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-400" />{reasons.backfill} backfill — leaver seats, keep funded</span>
          <span className="inline-flex items-center gap-1.5 text-sky-700"><span className="h-2 w-2 rounded-full bg-sky-400" />{reasons.new} new headcount</span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Filter by title, position ID, campus, department..."
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          className="pl-9 h-8 text-sm"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading vacancies...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              {total === 0
                ? `No ${category} vacant positions found.`
                : `No results matching "${searchFilter}"`
              }
            </div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-[11px]">
                    <TableHead className="w-[80px]">Position</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Job Family</TableHead>
                    <TableHead className="w-[50px]">Grade</TableHead>
                    <TableHead className="w-[60px]">Campus</TableHead>
                    <TableHead className="w-[80px]">Dept</TableHead>
                    <TableHead className="w-[60px]">Faculty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((v) => (
                    <TableRow key={v.id} className="text-xs">
                      <TableCell className="font-mono text-gray-500">{v.fo_position_id || v.position_code}</TableCell>
                      <TableCell className="font-medium">{v.title}</TableCell>
                      <TableCell className="text-gray-600">{v.job_family || '—'}</TableCell>
                      <TableCell>{v.grade_level || '—'}</TableCell>
                      <TableCell>
                        {v.dimension_campus
                          ? <span title={v.campus_name || ''}>{v.dimension_campus}</span>
                          : '—'
                        }
                      </TableCell>
                      <TableCell>{v.dimension_department || '—'}</TableCell>
                      <TableCell>{v.dimension_faculty || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {!loading && total > filtered.length && !searchFilter && (
            <div className="text-xs text-gray-400 text-center py-2 border-t">
              Showing {filtered.length} of {total} positions
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ── Stage shell bits ──────────────────────────────────────────────────────

const StageHeader: React.FC<{ title: string; sub?: string; actions?: React.ReactNode }> = ({ title, sub, actions }) => (
  <div className="flex items-baseline justify-between gap-3">
    <div>
      <h3 className="font-serif text-lg font-medium leading-tight">{title}</h3>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
    {actions}
  </div>
);

const FacetChip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      'rounded-full border px-3 py-1 text-xs transition-colors',
      active ? 'border-heritage-blue bg-heritage-blue text-white' : 'hover:bg-gray-50',
    )}
  >
    {children}
  </button>
);

// ── Component ────────────────────────────────────────────────────────────

const BudgetTab: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [importPreview, setImportPreview] = useState<BudgetImportPreviewResponse | null>(null);
  const [importResult, setImportResult] = useState<BudgetImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const [budgetYear, setBudgetYear] = useState<number | undefined>(undefined);
  const [budgetMonth, setBudgetMonth] = useState<number | undefined>(undefined);

  // Import, Reconcile and bulk position retirement rewrite or destroy the budget
  // baseline, so they stay with the budget role holders and are NOT grantable —
  // a budget:read/write grantee (e.g. Head of Finance) never sees them. The
  // server refuses them regardless; hiding just avoids dead-end buttons.
  const { user: budgetUser } = useAuth();
  const canBudgetAdmin = hasRole(budgetUser, BUDGET_ROLES);


  // Pipeline navigation — a grantee lands on Review, since Reconcile is hidden.
  // Stage survives refresh — landing back on Reconcile mid-review-session
  // was the complaint. Non-admins never restore into hidden stages.
  const [activeStage, setActiveStage] = useState<Stage>(() => {
    const saved = localStorage.getItem('budget.stage') as Stage | null;
    if (saved && canBudgetAdmin) return saved;
    if (saved === 'review' || saved === 'export') return saved;
    return canBudgetAdmin ? 'reconcile' : 'review';
  });
  useEffect(() => { localStorage.setItem('budget.stage', activeStage); }, [activeStage]);
  const [importMode, setImportMode] = useState<'rf1' | 'simple'>('rf1');
  type ReviewFacet = 'analytics' | 'pivot' | 'workbench' | 'disposition' | 'campus' | 'department' | 'faculty' | 'family' | 'employment' | 'vacancies';
  const [reviewFacet, setReviewFacet] = useState<ReviewFacet>(() =>
    (localStorage.getItem('budget.facet') as ReviewFacet | null) || 'analytics');
  useEffect(() => { localStorage.setItem('budget.facet', reviewFacet); }, [reviewFacet]);
  // Headcount overview (KPI cards + reconciliation bridge) collapses so the filterable
  // Budget vs Actuals analytics is the prominent default. Cost cards live in that view now.
  const [overviewOpen, setOverviewOpen] = useState(true);
  const [dispCounts, setDispCounts] = useState<Record<string, DispositionCount>>({});
  const [empCounts, setEmpCounts] = useState<Record<string, { count: number; annual: number }>>({});
  const [isExporting, setIsExporting] = useState(false);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await budgetService.getSummary({ year: budgetYear, month: budgetMonth });
      setSummary(data);
      if (!budgetYear && data.period_year) setBudgetYear(data.period_year);
      if (!budgetMonth && data.period_month) setBudgetMonth(data.period_month);
    } catch (error) {
      console.error('Failed to load budget summary:', error);
    } finally {
      setIsLoading(false);
    }
  }, [budgetYear, budgetMonth]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Disposition snapshot — the foot-to-gross bridge for Reconcile + Review +
  // the rail's live status. Best-effort; reads the persisted staging ledger.
  const loadDispSnapshot = useCallback(async () => {
    try {
      const resp = await budgetService.getUnlinkedItems({
        budgetYear: budgetYear ?? new Date().getFullYear(),
        includeLinked: true,
        includeDismissed: true,
        // savings/remove/special included so the gross foots to the xlsx export
        // (the 32 signed adjustment lines were invisible here — R722.3m vs R696.3m).
        status: 'auto_matched,manual,suggested,unmatched,dismissed,savings,remove,special',
      });
      setDispCounts(countsFromItems(resp.items || []));
      const ec: Record<string, { count: number; annual: number }> = {};
      for (const it of (resp.items || [])) {
        const cat = (it as { employment_category?: string }).employment_category || 'Unspecified';
        const annual = Object.values(it.monthly_amounts || {}).reduce((s: number, v) => s + (Number(v) || 0), 0);
        if (!ec[cat]) ec[cat] = { count: 0, annual: 0 };
        ec[cat].count += 1; ec[cat].annual += annual;
      }
      setEmpCounts(ec);
    } catch {
      /* snapshot is best-effort — rail shows "no import yet" */
    }
  }, [budgetYear]);

  useEffect(() => { loadDispSnapshot(); }, [loadDispSnapshot]);

  // ── Dimension sync ────────────────────────────────────────────────────

  const handleSyncDimensions = useCallback(async () => {
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      const res = await budgetService.syncDimensions();
      setSyncMessage({
        type: 'success',
        text: `${res.updated} positions updated from ${res.total_dimensions} F&O dimension records.`,
      });
      loadSummary();
    } catch (e: any) {
      setSyncMessage({
        type: 'error',
        text: e?.response?.data?.detail || e?.message || 'Sync failed',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [loadSummary]);

  // ── Import handlers (simple position-code import) ──────────────────────

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsImporting(true);
    setImportResult(null);
    try {
      const preview = await budgetService.previewImport(file);
      setImportPreview(preview);
    } catch (error) {
      console.error('Import preview failed:', error);
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview) return;
    setIsImporting(true);
    try {
      const result = await budgetService.confirmImport(importPreview);
      setImportResult(result);
      setImportPreview(null);
      loadSummary();
      loadDispSnapshot();
    } catch (error) {
      console.error('Import confirm failed:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // ── Export handlers ────────────────────────────────────────────────────

  const downloadBlob = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportReconciliation = async () => {
    const yr = budgetYear ?? new Date().getFullYear();
    setIsExporting(true);
    try {
      const blob = await budgetService.exportReconciliation(yr);
      downloadBlob(blob, `Budget_Reconciliation_${yr}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      console.error('Reconciliation export failed:', e);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportStaged = async () => {
    const yr = budgetYear ?? new Date().getFullYear();
    setIsExporting(true);
    try {
      const blob = await budgetService.exportBudgetItems(yr, { includeLinked: true, includeDismissed: false });
      downloadBlob(blob, `budget_${yr}_staged_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      console.error('Staged export failed:', e);
    } finally {
      setIsExporting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (isLoading && !summary) {
    return (
      <div className="py-12 text-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading budget data...
      </div>
    );
  }

  const periodLabel = summary
    ? `${MONTH_NAMES[summary.period_month]} ${summary.period_year}`
    : '';
  const yr = budgetYear ?? new Date().getFullYear();

  // Rail status + the mini-waterfall, derived from the disposition snapshot.
  const { g, grossCount } = groupCounts(dispCounts);
  const fmtM = (n: number) => `R${Math.round(n / 1e6)}m`;
  const reconcileSegs = grossCount
    ? GROUP_ORDER.map(k => ({ color: GROUP_FILL[k], pct: (g[k].count / grossCount) * 100 })).filter(s => s.pct > 0.5)
    : [];
  const railStages: RailStage[] = [
    { id: 'seed', label: 'Seed', icon: <Target className="h-4 w-4" />, status: summary ? `${formatNumber(summary.budgeted_positions)} budgeted · ${formatNumber(summary.unbudgeted_positions)} to cost` : 'establishment run-rate' },
    { id: 'import', label: 'Import', icon: <FileSpreadsheet className="h-4 w-4" />, status: 'RF1 + simple' },
    { id: 'reconcile', label: 'Reconcile', icon: <Edit3 className="h-4 w-4" />, status: grossCount ? `${formatNumber(g.funded.count)} of ${formatNumber(grossCount)} funded` : 'no import yet', segments: reconcileSegs },
    { id: 'review', label: 'Review', icon: <Building2 className="h-4 w-4" />, status: grossCount ? `${fmtM(g.funded.annual)} net funded` : 'budget at a glance' },
    { id: 'export', label: 'Export', icon: <Download className="h-4 w-4" />, status: g.quarantine.count > 0 ? `${formatNumber(g.quarantine.count)} to clear first` : 'reconciliation pack' },
  ].filter(s => canBudgetAdmin || (s.id !== 'import' && s.id !== 'reconcile'));
  const recommendedNext: Stage = grossCount === 0 ? 'import' : (g.quarantine.count + g.demand.count > 0 ? 'reconcile' : 'export');
  const breakdownsEmpty = !!summary && summary.by_campus.filter(r => r.code).length === 0;

  // Step one month at a time (rolls across year boundaries), so you don't have to open the
  // dropdowns to walk through the year. Anchors on the currently-displayed period.
  const stepMonth = (delta: number) => {
    let m = (budgetMonth ?? summary?.period_month ?? (new Date().getMonth() + 1)) + delta;
    let y = budgetYear ?? summary?.period_year ?? new Date().getFullYear();
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setBudgetYear(y);
    setBudgetMonth(m);
  };

  return (
    <div className="space-y-4">
      {/* Period selector — prev/next step through months + dropdowns to jump */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Budget period:</span>
          <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-0.5">
            <button type="button" onClick={() => stepMonth(-1)} title="Previous month"
              className="flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <Select
              value={budgetMonth ? String(budgetMonth) : (summary?.period_month ? String(summary.period_month) : '')}
              onValueChange={(v) => setBudgetMonth(Number(v))}
            >
              <SelectTrigger className="w-[110px] h-6 text-xs border-0 shadow-none focus:ring-0">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.slice(1).map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={budgetYear ? String(budgetYear) : ''}
              onValueChange={(v) => { setBudgetYear(Number(v)); setBudgetMonth(undefined); }}
            >
              <SelectTrigger className="w-[72px] h-6 text-xs border-0 shadow-none focus:ring-0">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => stepMonth(1)} title="Next month"
              className="flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <span className="text-xs text-gray-400 ml-1">{periodLabel}</span>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadSummary(); loadDispSnapshot(); }} disabled={isLoading}>
          <RefreshCw className={cn("h-3.5 w-3.5 mr-1", isLoading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Pipeline: rail + active stage */}
      <div className="flex items-start gap-4">
        <BudgetRail
          stages={railStages}
          active={activeStage}
          onSelect={(id) => setActiveStage(id as Stage)}
          recommendedNext={recommendedNext}
          footer={<div className="text-[11px] tabular-nums text-heritage-blue-300">{periodLabel || `${yr}`}</div>}
        />

        <div className="min-w-0 flex-1 space-y-4">

          {/* ① SEED — F&O-anchored, Sage-costed spine */}
          {activeStage === 'seed' && (
            <>
              <StageHeader title="Seed the establishment" sub="Every position gets a run-rate from F&O + Sage actuals — the spine the budget reconciles against." />
              <PositionBudgetView />
            </>
          )}

          {/* ② IMPORT — one drop-zone, two modes */}
          {canBudgetAdmin && activeStage === 'import' && (
            <>
              <StageHeader
                title="Import"
                sub="Bring in the Finance plan — the wizard's steps live here; reconciliation moves to the next stage."
                actions={
                  <div className="inline-flex overflow-hidden rounded-md border">
                    <button
                      onClick={() => setImportMode('rf1')}
                      className={cn('px-3 py-1.5 text-xs', importMode === 'rf1' ? 'bg-heritage-blue text-white' : 'hover:bg-gray-50')}
                    >RF1 worksheet</button>
                    <button
                      onClick={() => setImportMode('simple')}
                      className={cn('px-3 py-1.5 text-xs', importMode === 'simple' ? 'bg-heritage-blue text-white' : 'hover:bg-gray-50')}
                    >Simple position-code</button>
                  </div>
                }
              />

              {importMode === 'rf1' ? (
                <BudgetImportWizard onImportComplete={() => { loadSummary(); loadDispSnapshot(); }} />
              ) : (
                <Card>
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Upload className="h-4 w-4" /> Import budget from Excel
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Upload an Excel (.xlsx) or CSV file with columns:
                      <code className="bg-gray-100 px-1 rounded ml-1">position_code</code>,
                      <code className="bg-gray-100 px-1 rounded ml-1">budgeted_amount</code>.
                      Optional: <code className="bg-gray-100 px-1 rounded">year</code>,
                      <code className="bg-gray-100 px-1 rounded">month</code>,
                      <code className="bg-gray-100 px-1 rounded">budget_status</code>,
                      <code className="bg-gray-100 px-1 rounded">notes</code>.
                    </p>

                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer">
                        <Input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={isImporting}
                        />
                        <div className={cn(
                          "inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium",
                          "hover:bg-gray-50 cursor-pointer transition-colors",
                          isImporting && "opacity-50 cursor-not-allowed",
                        )}>
                          {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          Choose file
                        </div>
                      </label>
                    </div>

                    {importPreview && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <span className="font-medium">{importPreview.filename}</span>
                            <span className="text-gray-500 ml-2">
                              {importPreview.new_entries} new, {importPreview.updates} updates
                              {importPreview.errors > 0 && (
                                <span className="text-red-500 ml-1">, {importPreview.errors} errors</span>
                              )}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setImportPreview(null)}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleConfirmImport}
                              disabled={isImporting || importPreview.preview_rows.length === 0}
                            >
                              {isImporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                              Import {importPreview.new_entries + importPreview.updates} records
                            </Button>
                          </div>
                        </div>

                        {importPreview.error_details.length > 0 && (
                          <div className="bg-red-50 border border-red-100 rounded p-3">
                            <div className="text-xs font-medium text-red-700 mb-1">Errors:</div>
                            {importPreview.error_details.slice(0, 10).map((err, i) => (
                              <div key={i} className="text-xs text-red-600">{err}</div>
                            ))}
                          </div>
                        )}

                        <div className="max-h-[300px] overflow-y-auto border rounded">
                          <Table>
                            <TableHeader>
                              <TableRow className="text-xs bg-gray-50">
                                <TableHead>Position</TableHead>
                                <TableHead>Year</TableHead>
                                <TableHead>Month</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Action</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {importPreview.preview_rows.slice(0, 50).map((row) => (
                                <TableRow key={`${row.position_code}-${row.year}-${row.month}`} className="text-sm">
                                  <TableCell className="font-mono text-xs">{row.position_code}</TableCell>
                                  <TableCell>{row.year}</TableCell>
                                  <TableCell>{row.month}</TableCell>
                                  <TableCell className="text-right font-mono">{formatCurrency(row.budgeted_amount)}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-[10px]">{row.budget_status}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="outline"
                                      className={cn("text-[10px]",
                                        row.status === 'new' ? 'text-emerald-600 border-emerald-200' :
                                        row.status === 'update' ? 'text-blue-600 border-blue-200' :
                                        'text-red-600 border-red-200'
                                      )}
                                    >
                                      {row.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {importResult && (
                      <div className="bg-emerald-50 border border-emerald-100 rounded p-3">
                        <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
                          <CheckCircle className="h-4 w-4" />
                          Import complete: {importResult.created} created, {importResult.updated} updated
                          {importResult.errors > 0 && (
                            <span className="text-red-600">, {importResult.errors} errors</span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* ③ RECONCILE — docked bridge + triage workbench */}
          {canBudgetAdmin && activeStage === 'reconcile' && (
            <>
              <StageHeader title="Reconcile" sub="Match every RF1 line to a position — the bridge foots as you clear the queue." />
              {grossCount > 0 && <ReconciliationWaterfall counts={dispCounts} compact
                linesTitle="All staged RF1 worksheet lines for this year, incl. savings/removal adjustments — matches the Export for Finance workbook." />}
              <StagedItemsView
                budgetYear={yr}
                onMutated={() => { loadSummary(); loadDispSnapshot(); }}
                groupTotals={{ duplicate: g.duplicate.count, demand: g.demand.count, quarantine: g.quarantine.count }}
              />
            </>
          )}

          {/* ④ REVIEW — the budget, footed */}
          {activeStage === 'review' && (
            <>
              <StageHeader
                title="Review"
                sub="The budget at a glance — every rand accounted for."
                actions={
                  <Button variant="outline" size="sm" onClick={() => setActiveStage('export')}>
                    Export <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                }
              />

              {/* Collapsible headcount Overview — global position counts + reconciliation bridge.
                  Cost (budgeted/actual/variance) lives in the Budget vs Actuals view below to
                  avoid showing the same R-figure twice. */}
              <div className="rounded-lg border border-gray-200 bg-white">
                <button type="button" onClick={() => setOverviewOpen(o => !o)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left">
                  <div className="flex items-center gap-2">
                    {overviewOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <span className="text-sm font-medium text-gray-700">Overview</span>
                    {!overviewOpen && summary && (
                      <span className="text-xs text-gray-400">
                        {formatNumber(summary.total_positions)} F&O positions (incl. retired) · {formatNumber(summary.filled_positions)} filled · {formatNumber(summary.vacant_budgeted)} budgeted vacancies
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-400">{overviewOpen ? 'Hide' : 'Show'}</span>
                </button>
                {overviewOpen && (
                  <div className="space-y-3 px-3 pb-3">
                    {summary && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Card title="All F&O positions mirrored to the platform, incl. retired/stale seats — wider than the Seed spine (live seats only) and the staged RF1 lines."><CardContent className="p-3"><div className="text-2xl font-bold text-heritage-blue">{formatNumber(summary.total_positions)}</div><div className="text-xs text-gray-500">Total positions (all F&O)</div></CardContent></Card>
                        <Card><CardContent className="p-3"><div className="text-2xl font-bold text-emerald-600">{formatNumber(summary.budgeted_positions)}</div><div className="text-xs text-gray-500">Budgeted</div></CardContent></Card>
                        <Card><CardContent className="p-3"><div className="text-2xl font-bold text-gray-400">{formatNumber(summary.unbudgeted_positions)}</div><div className="text-xs text-gray-500">Unbudgeted</div></CardContent></Card>
                        <Card><CardContent className="p-3"><div className="text-2xl font-bold text-blue-600">{formatNumber(summary.filled_positions)}</div><div className="text-xs text-gray-500">Filled</div></CardContent></Card>
                        <Card><CardContent className="p-3"><div className="text-2xl font-bold text-amber-600">{formatNumber(summary.vacant_budgeted)}</div><div className="text-xs text-gray-500">Vacant (budgeted)</div></CardContent></Card>
                        <Card title="Vacant + unbudgeted — retire these in the Positions tab → Stale retirement"><CardContent className="p-3"><div className="text-2xl font-bold text-red-500">{formatNumber(summary.vacant_unbudgeted)}</div><div className="text-xs text-gray-500">Vacant (stale)</div></CardContent></Card>
                      </div>
                    )}
                    {grossCount > 0 && <ReconciliationWaterfall counts={dispCounts}
                      linesTitle="All staged RF1 worksheet lines for this year, incl. savings/removal adjustments — matches the Export for Finance workbook." />}
                  </div>
                )}
              </div>

              {syncMessage && (
                <div className={cn(
                  "rounded p-3 text-sm flex items-center gap-2",
                  syncMessage.type === 'success' ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-red-50 border border-red-100 text-red-700",
                )}>
                  {syncMessage.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
                  {syncMessage.text}
                  <button className="ml-auto text-xs underline opacity-60 hover:opacity-100" onClick={() => setSyncMessage(null)}>dismiss</button>
                </div>
              )}

              {/* Facet chips — Overview, Vacancies and variance collapse into one screen */}
              <div className="flex flex-wrap gap-2">
                <FacetChip active={reviewFacet === 'analytics'} onClick={() => setReviewFacet('analytics')}>Budget vs Actuals</FacetChip>
                <FacetChip active={reviewFacet === 'pivot'} onClick={() => setReviewFacet('pivot')}>Pivot</FacetChip>
                <FacetChip active={reviewFacet === 'workbench'} onClick={() => setReviewFacet('workbench')}>Workbench</FacetChip>
                <FacetChip active={reviewFacet === 'disposition'} onClick={() => setReviewFacet('disposition')}>By disposition</FacetChip>
                <FacetChip active={reviewFacet === 'campus'} onClick={() => setReviewFacet('campus')}>Campus</FacetChip>
                <FacetChip active={reviewFacet === 'department'} onClick={() => setReviewFacet('department')}>Department</FacetChip>
                <FacetChip active={reviewFacet === 'faculty'} onClick={() => setReviewFacet('faculty')}>Faculty</FacetChip>
                <FacetChip active={reviewFacet === 'family'} onClick={() => setReviewFacet('family')}>Job family</FacetChip>
                <FacetChip active={reviewFacet === 'employment'} onClick={() => setReviewFacet('employment')}>Employment type</FacetChip>
                <FacetChip active={reviewFacet === 'vacancies'} onClick={() => setReviewFacet('vacancies')}>Vacancies</FacetChip>
              </div>

              {reviewFacet === 'analytics' && (
                <BudgetVsActuals year={budgetYear} month={budgetMonth} />
              )}

              {reviewFacet === 'pivot' && (
                <BudgetPivot year={yr} />
              )}

              {reviewFacet === 'workbench' && (
                <BudgetWorkbench year={yr} />
              )}


              {reviewFacet === 'disposition' && (
                grossCount === 0 ? (
                  <div className="py-10 text-center text-gray-400 text-sm">
                    Run an import to see the reconciliation breakdown.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {GROUP_ORDER.map((grp) => {
                      const codes = Object.entries(dispCounts).filter(([c]) => (DISPOSITION_META[c as BudgetDisposition]?.group ?? 'quarantine') === grp);
                      if (!codes.length) return null;
                      return (
                        <Card key={grp}>
                          <CardContent className="p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: GROUP_FILL[grp] }} />{GROUP_LABEL[grp]}
                              </span>
                              <span className="text-xs tabular-nums text-muted-foreground">{formatNumber(g[grp].count)} · {formatCurrency(g[grp].annual)}</span>
                            </div>
                            <div className="space-y-1">
                              {codes.sort((a, b) => Math.abs(b[1].annual) - Math.abs(a[1].annual)).map(([code, v]) => (
                                <div key={code} className="flex items-center justify-between text-xs">
                                  <DispositionChip disposition={code} />
                                  <span className="tabular-nums text-muted-foreground">{formatNumber(v.count)} · {formatCurrency(v.annual)}</span>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )
              )}

              {(reviewFacet === 'campus' || reviewFacet === 'department' || reviewFacet === 'faculty' || reviewFacet === 'family') && (
                breakdownsEmpty ? (
                  <div className="bg-blue-50 border border-blue-100 rounded p-3 flex items-center justify-between">
                    <div className="text-sm text-blue-700">Financial Dimensions (Campus / Department / Faculty) have not been synced yet.</div>
                    <Button variant="outline" size="sm" onClick={handleSyncDimensions} disabled={isSyncing}>
                      {isSyncing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      {isSyncing ? 'Syncing...' : 'Sync from F&O'}
                    </Button>
                  </div>
                ) : (
                  <>
                    {reviewFacet === 'campus' && <BreakdownTable title="Campus" data={summary?.by_campus ?? []} />}
                    {reviewFacet === 'department' && <BreakdownTable title="Department" data={summary?.by_department ?? []} />}
                    {reviewFacet === 'faculty' && <BreakdownTable title="Faculty" data={summary?.by_faculty ?? []} />}
                    {reviewFacet === 'family' && <BreakdownTable title="Job Family" data={summary?.by_function ?? []} />}
                  </>
                )
              )}

              {reviewFacet === 'employment' && (
                Object.keys(empCounts).length === 0 ? (
                  <div className="py-10 text-center text-gray-400 text-sm">Run an import to see the employment-type split.</div>
                ) : (
                  <Card>
                    <CardContent className="p-3 space-y-1.5">
                      <div className="mb-1 text-xs text-muted-foreground">Budget by employment type (from the uploaded RF1 payroll type). ICA &amp; Casual are typically contractor / sessional spend.</div>
                      {EMP_ORDER.filter((c) => empCounts[c]).map((cat) => {
                        const v = empCounts[cat];
                        const tot = Object.values(empCounts).reduce((s, x) => s + Math.abs(x.annual), 0) || 1;
                        const pct = (Math.abs(v.annual) / tot) * 100;
                        return (
                          <div key={cat} className="flex items-center justify-between text-xs">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ring-1 ${EMP_TONE[cat] || 'bg-slate-50 text-slate-600 ring-slate-200'}`}>{cat}</span>
                            <span className="tabular-nums text-muted-foreground">{formatNumber(v.count)} · {formatCurrency(v.annual)} · {pct.toFixed(0)}%</span>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )
              )}

              {reviewFacet === 'vacancies' && (
                <VacanciesPanel
                  vacantBudgeted={summary?.vacant_budgeted ?? 0}
                  vacantUnbudgeted={summary?.vacant_unbudgeted ?? 0}
                  budgetYear={budgetYear}
                  budgetMonth={budgetMonth}
                />
              )}
            </>
          )}

          {/* ⑤ EXPORT — readiness gate + Finance pack */}
          {activeStage === 'export' && (
            <>
              <StageHeader title="Export for Finance" sub="A signable reconciliation — the pipeline's terminus." />
              <Card>
                <CardContent className="p-4 space-y-2">
                  {grossCount === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 shrink-0" /> No RF1 import found for {yr} — import a worksheet first.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <CircleCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                        Bridge foots to gross ({formatCurrency(g.funded.annual + g.demand.annual + g.duplicate.annual + g.adjustment.annual + g.quarantine.annual)})
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CircleCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                        {formatNumber(g.funded.count)} of {formatNumber(grossCount)} lines funded
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {g.quarantine.count > 0
                          ? <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                          : <CircleCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
                        {g.quarantine.count > 0
                          ? <>{formatNumber(g.quarantine.count)} line(s) in quarantine — review before sign-off
                              <button className="ml-auto text-xs text-heritage-blue underline" onClick={() => setActiveStage('reconcile')}>Fix in Reconcile</button></>
                          : <>No lines in quarantine</>}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground">
                Sheets: Reconciliation Bridge · Employees (Jan–Dec actual vs budget per person) · Vacant months · Positions · Duplicates · Terminations · Budget-without-position · Positions-without-budget · Paid-unbudgeted · High-variance · Allocation issues · Change ledger · Seat requests
              </p>
              <div className="flex flex-wrap gap-2">
                {/* The reconciliation pack answers organisation-level questions -
                    workers on more than one seat, staff allocated to NO position -
                    which do not decompose by department. An orphan worker has no
                    department, so there is no scoped version of this to give. The
                    server refuses it; hiding it stops a department-scoped viewer
                    clicking into an error they can do nothing about. */}
                {canBudgetAdmin && (
                  <Button onClick={handleExportReconciliation} disabled={isExporting}>
                    {isExporting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                    Export reconciliation .xlsx
                  </Button>
                )}
                <Button variant="outline" onClick={handleExportStaged} disabled={isExporting}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Staged-items snapshot
                </Button>
              </div>
              {!canBudgetAdmin && (
                <p className="text-xs text-muted-foreground">
                  The reconciliation pack covers the whole organisation and is not
                  available on a department limit. The staged-items snapshot above,
                  and the exports on Budget vs Actuals and Pivot, cover your
                  departments.
                </p>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default BudgetTab;
